// Quick-add inline syntax (spec §8.6). Pure; unit-tested in parse.test.ts.
//   #key:value  label          @project   project (fuzzy, resolved by caller)
//   !1-3        priority       ^date      due
//   >date       scheduled      ~30        estimate minutes
//   +slug       workspace      leading emoji → icon
import type { QuickAddResult } from '@/kinds/kind'
import { parseFriendlyDate } from '@/lib/time'
import { splitLeadingEmoji } from '@/lib/colour'
import type { TaskSpec } from './schema'

export const quickAddParse = (text: string, today?: string): QuickAddResult<TaskSpec> => {
  const spec: Partial<TaskSpec> = {}
  const labels: Record<string, string> = {}
  let project: string | undefined
  let workspace: string | undefined
  const words: string[] = []
  for (const tok of text.trim().split(/\s+/)) {
    if (!tok) continue
    let m: RegExpExecArray | null
    if ((m = /^#([a-z0-9_-]+):(\S+)$/i.exec(tok))) {
      labels[m[1]!.toLowerCase()] = m[2]!
      continue
    }
    if ((m = /^@(\S+)$/.exec(tok))) {
      project = m[1]!.replace(/[-_]/g, ' ')
      continue
    }
    if ((m = /^\+([a-z0-9][a-z0-9-]{0,30})$/.exec(tok))) {
      workspace = m[1]!
      continue
    }
    if ((m = /^!([0-3])$/.exec(tok))) {
      spec.priority = Number(m[1])
      continue
    }
    if ((m = /^~(\d{1,4})(m|min|h)?$/i.exec(tok))) {
      const n = Number(m[1])
      spec.estimate_minutes = m[2]?.toLowerCase() === 'h' ? n * 60 : n
      continue
    }
    if ((m = /^\^(\S+)$/.exec(tok))) {
      const d = parseFriendlyDate(m[1]!, today)
      if (d) {
        spec.due = d
        continue
      }
    }
    if ((m = /^>(\S+)$/.exec(tok))) {
      const d = parseFriendlyDate(m[1]!, today)
      if (d) {
        spec.scheduled = d
        continue
      }
    }
    words.push(tok)
  }
  const { icon, title } = splitLeadingEmoji(words.join(' '))
  spec.title = title
  if (icon) spec.icon = icon
  const out: QuickAddResult<TaskSpec> = { spec }
  if (Object.keys(labels).length) out.labels = labels
  if (project) out.project = project
  if (workspace) out.workspace = workspace
  return out
}

/** Split a multi-line paste into candidate task lines (brain dump). */
export const splitLines = (text: string): string[] =>
  text
    .split(/\r?\n/)
    .map((l) => l.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, '').trim())
    .filter(Boolean)
